import axios from "axios";

export const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const client = axios.create({ baseURL: API_URL });

const UNREACHABLE = {
  message: `Could not reach the analysis service at ${API_URL}. Start it with \`uvicorn api:app --reload\` and try again.`,
  canRetry: true,
};

function isAbort(error) {
  return (
    error?.name === "AbortError" ||
    error?.code === "ERR_CANCELED" ||
    axios.isCancel?.(error)
  );
}

/**
 * Turns any failure into a message that states the cause and the way out, so
 * every error surface can offer a recovery path rather than "Server error".
 */
export function readError(error) {
  if (isAbort(error)) {
    return { message: "Run cancelled.", canRetry: true };
  }
  if (error?.code === "ECONNABORTED") {
    return {
      message:
        "The request timed out. Rendering and projecting a long document takes a while — try again, or use a shorter file.",
      canRetry: true,
    };
  }

  // Raised by the streaming reader, which carries its own message.
  if (error?.isServiceError) {
    return { message: error.message, canRetry: error.canRetry !== false };
  }
  if (error instanceof TypeError) {
    return UNREACHABLE;
  }
  if (!error?.response) {
    return UNREACHABLE;
  }

  const { status, data } = error.response;
  let detail = data?.detail;
  if (!detail && data instanceof ArrayBuffer) {
    try {
      detail = JSON.parse(new TextDecoder().decode(data))?.detail;
    } catch {
      detail = undefined;
    }
  }
  return mapStatus(status, detail);
}

function mapStatus(status, detail) {
  if (status === 400) {
    return {
      message: detail || "That file type is not supported. Use a PDF, or a TXT for comparison.",
      canRetry: false,
    };
  }
  if (status === 404) {
    return {
      message: detail || "That report is no longer available. Run the comparison again to regenerate it.",
      canRetry: false,
    };
  }
  if (status === 413) {
    return { message: "That file is too large for the service. Try a smaller document.", canRetry: false };
  }
  return {
    message: detail || `The service returned an unexpected error (HTTP ${status}). Try again in a moment.`,
    canRetry: true,
  };
}

function serviceError(message, canRetry = true) {
  const error = new Error(message);
  error.isServiceError = true;
  error.canRetry = canRetry;
  return error;
}

export async function checkHealth(signal) {
  const res = await client.get("/", { signal, timeout: 6000 });
  return res.data;
}

/**
 * POST a multipart body and consume the newline-delimited JSON progress stream
 * the backend writes back.
 *
 * `fetch` is used rather than axios because only the streams API exposes the
 * response body incrementally in a browser; axios buffers it. Each line is one
 * event: `stage`, `log`, `result` or `error`. Resolves with the payload of the
 * single terminal `result` event.
 */
async function streamNdjson(path, body, { signal, onEvent } = {}) {
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, { method: "POST", body, signal });
  } catch (error) {
    if (isAbort(error)) throw error;
    throw serviceError(UNREACHABLE.message, true);
  }

  if (!response.ok) {
    let detail;
    try {
      detail = (await response.json())?.detail;
    } catch {
      detail = undefined;
    }
    const mapped = mapStatus(response.status, detail);
    throw serviceError(mapped.message, mapped.canRetry);
  }
  if (!response.body) {
    throw serviceError("This browser cannot read streaming responses.", false);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result = null;

  const handleLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let event;
    try {
      event = JSON.parse(trimmed);
    } catch {
      return; // Ignore a partial or malformed line rather than failing the run.
    }
    if (event.event === "error") {
      throw serviceError(event.message || "The analysis failed.", true);
    }
    if (event.event === "result") {
      result = event.data;
    }
    onEvent?.(event);
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf("\n");
    while (newline >= 0) {
      const line = buffer.slice(0, newline);
      buffer = buffer.slice(newline + 1);
      handleLine(line);
      newline = buffer.indexOf("\n");
    }
  }
  handleLine(buffer + decoder.decode());

  if (!result) {
    throw serviceError("The service closed the connection before returning a result.", true);
  }
  return result;
}

export function compareStream({ fileOne, fileTwo, signal, onEvent }) {
  const body = new FormData();
  body.append("fileOne", fileOne);
  body.append("fileTwo", fileTwo);
  return streamNdjson("/compare/stream", body, { signal, onEvent });
}

export function injectionStream({ file, signal, onEvent }) {
  const body = new FormData();
  body.append("fileOne", file);
  return streamNdjson("/Character-Injection/stream", body, { signal, onEvent });
}

/** Non-streaming equivalents, kept for callers that only want the final result. */
export async function compareDocuments({ fileOne, fileTwo, signal }) {
  const body = new FormData();
  body.append("fileOne", fileOne);
  body.append("fileTwo", fileTwo);
  const res = await client.post("/compare/", body, { signal, timeout: 300000 });
  return res.data;
}

export async function checkInjection({ file, signal }) {
  const body = new FormData();
  body.append("fileOne", file);
  const res = await client.post("/Character-Injection/", body, { signal, timeout: 300000 });
  return res.data;
}

export function reportUrl(reportId) {
  return `${API_URL}/report/${encodeURIComponent(reportId)}`;
}

/**
 * The bundled sample documents, served as static assets from `public/samples/`.
 *
 * Deliberately not fetched from the API. Samples are the one thing that has to
 * keep working when the backend is down, since that is exactly when they are
 * offered as the fallback. Copies of the fixtures in `tests/fixtures/`, kept in
 * step by `npm run sync:samples`.
 */
export const SAMPLES = [
  {
    id: "clean",
    role: "clean",
    filename: "paper_clean.pdf",
    label: "Original",
    description: "The source paragraph, 73 words, nothing hidden in it.",
  },
  {
    id: "injected",
    role: "injected",
    filename: "paper_injected_every.pdf",
    label: "Padded copy",
    description: "The same text with an 'i' hidden before all 73 words.",
  },
];

export function sampleUrl(filename) {
  return `${process.env.PUBLIC_URL || ""}/samples/${filename}`;
}

export function findSample(role) {
  return SAMPLES.find((sample) => sample.role === role);
}

/** Fetch a sample from the static assets and hand it back as a `File`. */
export async function fetchSampleFile(sample, signal) {
  const res = await fetch(sampleUrl(sample.filename), { signal });
  if (!res.ok) {
    throw serviceError(`Could not load the sample document (HTTP ${res.status}).`, false);
  }
  const blob = await res.blob();
  return new File([blob], sample.filename, { type: "application/pdf" });
}

/**
 * Frozen results for the sample documents, captured verbatim from the real API.
 *
 * Used only when the backend is unreachable and the loaded files are the
 * samples, so the demo still shows a genuine result rather than an error. Never
 * substituted for a document the user supplied: that would present the sample's
 * numbers as if they described their file.
 */
export async function frozenCompare() {
  const { default: data } = await import("../mocks/compare.json");
  return data;
}

export async function frozenInjection() {
  const { default: data } = await import("../mocks/injection.json");
  return data;
}
